<template>
  <button
    class="flex w-10 h-10 px-1 justify-center items-center border-none rounded-full relative"
    :class="[
      { 'pointer-events-none': succeded || isCapturing || isCountdown },
      isCountdown ? 'bg-vice-yellow' : 'sm:bg-vice-teal-600 hover:bg-vice-teal-700',
    ]"
    @click="captureSnapshot"
  >
    <ButtonCountdown v-if="isCountdown" :countdownValue="countdownValue" />
    <ViceLoader colorClass="text-vice-light-grey" class="relative left-2" v-if="isCapturing" />
    <SnapshotCameraIcon v-if="!isCapturing && !succeded && !isCountdown" class="w-6 h-6" />
    <TickIcon v-if="succeded" class="w-8 h-8" />
  </button>
</template>

<script>
import { ref, onUnmounted } from 'vue';
import TickIcon from '@/Components/Icons/TickIcon.vue';
import useSessionStore from '@/Stores/useSessionStore';
import handleAsyncRequest from '@/Utils/handleAsyncRequest';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import SnapshotCameraIcon from '@/Components/Icons/SnapshotCameraIcon.vue';
import ButtonCountdown from '@/Components/Buttons/ButtonCountdown.vue';

export default {
  name: 'SnapshotButton',
  props: {
    jitsiId: {
      type: String,
      required: true,
    },
    sessionId: {
      type: Number,
      required: true,
    },
    deviceName: {
      type: String,
      required: true,
    },
  },
  emits: ['snapshot-taken'],
  components: { ButtonCountdown, SnapshotCameraIcon, TickIcon, ViceLoader },
  setup(props, { emit }) {
    const succeded = ref(false);
    const isCapturing = ref(false);
    const sessionStore = useSessionStore();

    // Countdown state
    const isCountdown = ref(false);
    const countdownValue = ref(3);
    const countdownTimerRef = ref(null);

    const startCountdown = (seconds = 3) =>
      new Promise((resolve, reject) => {
        try {
          if (countdownTimerRef.value) {
            clearInterval(countdownTimerRef.value);
            countdownTimerRef.value = null;
          }
          countdownValue.value = seconds;
          isCountdown.value = true;
          countdownTimerRef.value = setInterval(() => {
            if (countdownValue.value <= 1) {
              clearInterval(countdownTimerRef.value);
              countdownTimerRef.value = null;
              isCountdown.value = false;
              resolve(true);
            } else {
              countdownValue.value -= 1;
            }
          }, 1000);
        } catch (e) {
          isCountdown.value = false;
          reject(e);
        }
      });

    onUnmounted(() => {
      if (countdownTimerRef.value) {
        clearInterval(countdownTimerRef.value);
        countdownTimerRef.value = null;
      }
      isCountdown.value = false;
    });

    const captureSnapshot = async () => {
      try {
        // Show countdown before capturing
        await startCountdown(3);

        isCapturing.value = true;

        const videoElement = document.querySelector(`video[data-user-id="${props.jitsiId}"]`);
        if (!videoElement) console.log('Error: Stream not found');

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        canvas.width = videoElement.videoWidth || 1920;
        canvas.height = videoElement.videoHeight || 1080;

        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, 'image/jpeg', 0.95);
        });

        await saveSnapshot(blob, props.jitsiId);
      } catch (error) {
        console.error('Snapshot capture failed:', error);
      } finally {
        isCapturing.value = false;
        succeded.value = true;

        // Open the sidebar if closed
        if (!sessionStore.isOpenSidebar) sessionStore.toggleSidebar();
        sessionStore.sidebarTab = 'sessionTab';

        setTimeout(() => {
          succeded.value = false;
        }, 1000);
      }
    };

    const saveSnapshot = async (blob, streamId) => {
      const formData = new FormData();
      formData.append('snapshot', blob, `snapshot-${Date.now()}.jpg`);
      formData.append('stream_id', streamId);
      formData.append('session_id', props.sessionId);
      formData.append('timestamp', Date.now());
      formData.append('device_type', props.deviceName);

      const [res, err] = await handleAsyncRequest(saveRequest(formData));
      if (!err) {
        sessionStore.addSnapshot(res.snapshot);
        emit('snapshot-taken', { jitsiId: props.jitsiId, timestamp: Date.now() });
      } else {
        console.error('Error saving snapshot:', err);
      }
    };

    const saveRequest = async (formData) => {
      try {
        const res = await fetch(route('snapshot.save'), {
          method: 'POST',
          body: formData,
        });
        return res.json();
      } catch (error) {
        console.error('Error saving snapshot request:', error);
        throw error.response;
      }
    };

    return {
      succeded,
      isCapturing,
      isCountdown,
      countdownValue,
      captureSnapshot,
    };
  },
};
</script>
